import type { MaybeRefOrGetter } from 'vue';
import type { Filter } from '#layers/data-model/public';
import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';
import { useAuth } from '#layers/access/public';
import { CURRENT_USER_VARIABLE, filterContainsVariable, substituteFilterVariables } from '#layers/data-model/public';

/**
 * Resolves dynamic filter variables to runtime values before a filter is applied.
 * Currently just `$CURRENT_USER` → the logged-in user's id.
 *
 * `ready` gates on the current user's id, which may not be loaded yet.
 */
export function useDynamicVariableFilter(filter: MaybeRefOrGetter<Filter | undefined>) {
	const { currentUser } = storeToRefs(useAuth());

	const currentUserId = computed(() => currentUser.value?.id || undefined);
	const rawFilter = computed(() => toValue(filter));
	const containsCurrentUser = computed(() => filterContainsVariable(rawFilter.value, CURRENT_USER_VARIABLE));

	const ready = computed(() => !containsCurrentUser.value || currentUserId.value != null);

	const resolvedFilter = computed(() => {
		const raw = rawFilter.value;
		// Short-circuit to the original filter (identity) when there's no variable to resolve.
		if (!raw || !containsCurrentUser.value || currentUserId.value == null) return raw;

		return substituteFilterVariables(raw, { [CURRENT_USER_VARIABLE]: currentUserId.value });
	});

	return { filter: resolvedFilter, ready };
}
